import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { ArrowLeft, Plus, Zap, Scale, Heart, Utensils, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import api from '../api';
import { Animal, WeightRecord, FeedRecommendation } from '../types';

const BREED_LABELS: Record<string, string> = {
  qorabosh: 'Qorabosh', golshtin: 'Golshtin', simmental: 'Simmental',
  abuerdeen: 'Aberdeen Angus', mahalliy: 'Mahalliy zot',
};

export default function AnimalDetail({ animalId, onBack }: { animalId: number; onBack: () => void }) {
  const [animal, setAnimal] = useState<Animal | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'overview' | 'weight' | 'feed' | 'health'>('overview');
  const [modal, setModal] = useState<'weight' | 'feed' | 'health' | null>(null);
  const [recLoading, setRecLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.getAnimal(animalId).then(r => { setAnimal(r.data); setLoading(false); });
  };

  useEffect(() => { load(); }, [animalId]);

  const handleGenRec = async () => {
    setRecLoading(true);
    await api.generateRecommendation(animalId);
    await load();
    setRecLoading(false);
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!animal) return null;

  const rec = animal.recommendations?.[0];
  const weightHistory = animal.weight_records || [];
  const chartData = [...weightHistory].reverse().map(w => ({
    date: new Date(w.recorded_at).toLocaleDateString('uz', { month: 'short', day: 'numeric' }),
    weight: w.weight,
  }));

  return (
    <div className="page-body">
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <button className="btn btn-outline btn-sm" onClick={onBack}><ArrowLeft size={14} /> Orqaga</button>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700 }}>{animal.tag_id}</h2>
          <span className="tag-id">{animal.tag_id}</span>
          <span className={`badge badge-${animal.status}`} style={{ marginLeft: 8 }}>{animal.status}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setModal('weight')}><Scale size={13} /> Vazn</button>
          <button className="btn btn-outline btn-sm" onClick={() => setModal('feed')}><Utensils size={13} /> Yem</button>
          <button className="btn btn-outline btn-sm" onClick={() => setModal('health')}><Heart size={13} /> Sog'liq</button>
          <button className="btn btn-primary btn-sm" onClick={handleGenRec} disabled={recLoading}>
            <Zap size={13} />{recLoading ? '...' : 'AI Tavsiya'}
          </button>
        </div>
      </div>

      {/* Sell ready banner */}
      {rec?.sell_ready && (
        <div className="sell-banner">
          <CheckCircle size={16} />
          Bu chorva sotishga tayyor! Maqsad vazn: {rec.target_weight} kg | Hozirgi: {animal.latest_weight} kg
        </div>
      )}

      {/* Stats strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Hozirgi vazn', val: `${animal.latest_weight} kg`, color: 'var(--teal)' },
          { label: "Jami o'sish", val: `+${animal.total_gain} kg`, color: 'var(--green)' },
          { label: 'Kunlik ADG', val: animal.daily_gain > 0 ? `${animal.daily_gain} kg` : '—', color: 'var(--teal-light)' },
          { label: 'Yoshi', val: `${animal.age_months} oy`, color: 'var(--amber)' },
          { label: 'Zot', val: BREED_LABELS[animal.breed] || animal.breed, color: 'var(--gray)' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ borderTop: `3px solid ${s.color}` }}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ fontSize: 18, color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tabs">
        {(['overview', 'weight', 'feed', 'health'] as const).map(t => (
          <button key={t} className={`tab-btn ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'overview' ? 'Umumiy' : t === 'weight' ? 'Vazn tarixi' : t === 'feed' ? 'Yem yozuvlari' : 'Sog\'liq'}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* AI Recommendation */}
          <div className="card">
            <div className="card-header"><h3>AI Ratsion Tavsiyasi</h3><Zap size={15} style={{ color: 'var(--amber)' }} /></div>
            <div className="card-body">
              {rec ? (
                <>
                  <div className="rec-metrics">
                    <div className="rec-metric">
                      <div className="rm-val">{rec.daily_feed_kg}</div>
                      <div className="rm-label">kg/kun yem</div>
                    </div>
                    <div className="rec-metric">
                      <div className="rm-val">{rec.fcr}</div>
                      <div className="rm-label">FCR</div>
                    </div>
                    <div className="rec-metric">
                      <div className="rm-val">{rec.days_remaining}</div>
                      <div className="rm-label">kun qoldi</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div style={{ fontSize: 13, color: 'var(--gray)' }}>
                      Kutilayotgan kunlik o'sish:
                      <strong style={{ color: 'var(--teal)', marginLeft: 4 }}>{rec.expected_daily_gain} kg</strong>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--gray)' }}>
                      Maqsad vazn:
                      <strong style={{ color: 'var(--text-dark)', marginLeft: 4 }}>{rec.target_weight} kg</strong>
                    </div>
                  </div>
                  {rec.notes && (
                    <div className="alert alert-amber" style={{ marginTop: 12, fontSize: 12 }}>
                      <AlertTriangle size={13} />{rec.notes}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 8 }}>
                    Oxirgi yangilanish: {new Date(rec.generated_at).toLocaleString('uz')}
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>Tavsiya mavjud emas</p>
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={handleGenRec}>
                    <Zap size={13} /> Yaratish
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Weight chart */}
          <div className="card">
            <div className="card-header"><h3>Vazn grafigi</h3><TrendingUp size={15} style={{ color: 'var(--teal)' }} /></div>
            <div className="card-body">
              {chartData.length > 1 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                    <Tooltip formatter={(v: any) => [`${v} kg`, 'Vazn']} />
                    {rec && <ReferenceLine y={rec.target_weight} stroke="#E5831A" strokeDasharray="4 4" label={{ value: 'Maqsad', fontSize: 11, fill: '#E5831A' }} />}
                    <Line type="monotone" dataKey="weight" stroke="#0D7E6E" strokeWidth={2.5} dot={{ fill: '#0D7E6E', r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <div className="empty-state"><p>Yetarli ma'lumot yo'q</p></div>}
            </div>
          </div>

          {/* Animal info */}
          <div className="card">
            <div className="card-header"><h3>Ma'lumotlar</h3></div>
            <div className="card-body">
              <div className="detail-grid">
                {[
                  { label: 'Tag ID', val: animal.tag_id },
                  { label: 'Zot', val: BREED_LABELS[animal.breed] },
                  { label: 'Jinsi', val: animal.gender === 'erkak' ? '♂ Erkak' : '♀ Urg\'oq' },
                  { label: "Tug'ilgan", val: animal.birth_date },
                  { label: "Boshlang'ich vazn", val: `${animal.initial_weight} kg` },
                ].map((d, i) => (
                  <div key={i} className="detail-item">
                    <div className="di-label">{d.label}</div>
                    <div className="di-value">{d.val}</div>
                  </div>
                ))}
              </div>
              {animal.notes && (
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--gray-light)', borderRadius: 8, fontSize: 13, color: 'var(--gray)' }}>
                  {animal.notes}
                </div>
              )}
            </div>
          </div>

          {/* Last health */}
          <div className="card">
            <div className="card-header"><h3>So'nggi sog'liq yozuvi</h3></div>
            <div className="card-body">
              {animal.health_records?.length ? (
                <div>
                  {animal.health_records.slice(0, 3).map(h => (
                    <div key={h.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--gray-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <strong style={{ fontSize: 13 }}>{h.diagnosis}</strong>
                        <span className={`badge badge-${h.severity}`}>{h.severity}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray)' }}>
                        {h.date} · {h.record_type}
                        {h.cost > 0 && ` · ${h.cost.toLocaleString()} so'm`}
                        {h.resolved && <span style={{ color: 'var(--green)', marginLeft: 6 }}>✓ Yaxshilandi</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="empty-state"><p>Sog'liq yozuvi yo'q</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* Weight Tab */}
      {tab === 'weight' && (
        <div className="card">
          <div className="card-header">
            <h3>Barcha vazn o'lchovlari</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('weight')}><Plus size={13} /> Qo'shish</button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>#</th><th>Sana</th><th>Vazn (kg)</th><th>O'zgarish</th><th>Izoh</th></tr></thead>
              <tbody>
                {[...weightHistory].reverse().map((w, i, arr) => {
                  const prev = arr[i - 1];
                  const diff = prev ? w.weight - prev.weight : null;
                  return (
                    <tr key={w.id}>
                      <td style={{ color: 'var(--gray)', fontSize: 12 }}>{i + 1}</td>
                      <td>{new Date(w.recorded_at).toLocaleDateString('uz')}</td>
                      <td><strong>{w.weight} kg</strong></td>
                      <td>{diff !== null ? (
                        <span style={{ color: diff >= 0 ? 'var(--teal)' : 'var(--red)', fontWeight: 600 }}>
                          {diff >= 0 ? '+' : ''}{diff.toFixed(1)} kg
                        </span>
                      ) : '—'}</td>
                      <td style={{ color: 'var(--gray)', fontSize: 12 }}>{w.notes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {weightHistory.length === 0 && <div className="empty-state"><p>Vazn o'lchovi yo'q</p></div>}
          </div>
        </div>
      )}

      {/* Feed Tab */}
      {tab === 'feed' && (
        <div className="card">
          <div className="card-header">
            <h3>Yem yozuvlari</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('feed')}><Plus size={13} /> Qo'shish</button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Sana</th><th>Yem turi</th><th>Tavsiya (kg)</th><th>Berilgan (kg)</th><th>Samaradorlik</th><th>Xarajat</th></tr></thead>
              <tbody>
                {(animal.feed_records || []).map(f => {
                  const eff = f.recommended_kg > 0 ? Math.round((f.given_kg / f.recommended_kg) * 100) : 100;
                  return (
                    <tr key={f.id}>
                      <td>{f.date}</td>
                      <td>{f.feed_type}</td>
                      <td style={{ color: 'var(--teal)' }}>{f.recommended_kg} kg</td>
                      <td><strong>{f.given_kg} kg</strong></td>
                      <td>
                        <span style={{ color: eff >= 95 && eff <= 105 ? 'var(--green)' : 'var(--amber)', fontWeight: 600 }}>
                          {eff}%
                        </span>
                      </td>
                      <td>{f.daily_cost.toLocaleString()} so'm</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(animal.feed_records?.length || 0) === 0 && <div className="empty-state"><p>Yem yozuvi yo'q</p></div>}
          </div>
        </div>
      )}

      {/* Health Tab */}
      {tab === 'health' && (
        <div className="card">
          <div className="card-header">
            <h3>Sog'liq tarixi</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('health')}><Plus size={13} /> Qo'shish</button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Sana</th><th>Tur</th><th>Tashxis</th><th>Og'irlik</th><th>Xarajat</th><th>Holat</th><th>Shifokor</th></tr></thead>
              <tbody>
                {(animal.health_records || []).map(h => (
                  <tr key={h.id}>
                    <td>{h.date}</td>
                    <td>{h.record_type}</td>
                    <td><strong>{h.diagnosis}</strong>{h.treatment && <div style={{ fontSize: 11, color: 'var(--gray)' }}>{h.treatment}</div>}</td>
                    <td><span className={`badge badge-${h.severity}`}>{h.severity}</span></td>
                    <td>{h.cost > 0 ? `${h.cost.toLocaleString()} so'm` : '—'}</td>
                    <td>{h.resolved ? <span style={{ color: 'var(--green)', fontSize: 12 }}>✓ Yaxshilandi</span> : <span style={{ color: 'var(--amber)', fontSize: 12 }}>Davom etmoqda</span>}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray)' }}>{h.veterinarian || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(animal.health_records?.length || 0) === 0 && <div className="empty-state"><p>Sog'liq yozuvi yo'q</p></div>}
          </div>
        </div>
      )}

      {/* Modals */}
      {modal === 'weight' && (
        <WeightModal animalId={animalId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal === 'feed' && (
        <FeedModal animalId={animalId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal === 'health' && (
        <HealthModal animalId={animalId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function WeightModal({ animalId, onClose, onSaved }: any) {
  const [weight, setWeight] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await api.addWeight(animalId, { weight: parseFloat(weight), notes });
    onSaved();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="modal-header"><h3>Vazn qo'shish</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Vazn (kg) *</label>
              <input type="number" className="form-input" value={weight} onChange={e => setWeight(e.target.value)} required step="0.1" min="50" max="1000" placeholder="300" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Izoh</label>
              <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Ixtiyoriy" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Bekor</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '...' : 'Saqlash'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FeedModal({ animalId, onClose, onSaved }: any) {
  const [form, setForm] = useState({ recommended_kg: '', given_kg: '', feed_type: 'Aralash yem', cost_per_kg: '2200', date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await api.addFeed(animalId, { ...form, recommended_kg: parseFloat(form.recommended_kg), given_kg: parseFloat(form.given_kg), cost_per_kg: parseFloat(form.cost_per_kg) });
    onSaved();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Yem yozuvi qo'shish</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tavsiya (kg) *</label>
                <input type="number" className="form-input" value={form.recommended_kg} onChange={e => set('recommended_kg', e.target.value)} required step="0.01" min="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Berilgan (kg) *</label>
                <input type="number" className="form-input" value={form.given_kg} onChange={e => set('given_kg', e.target.value)} required step="0.01" min="0" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Yem turi</label>
                <select className="form-select" value={form.feed_type} onChange={e => set('feed_type', e.target.value)}>
                  {['Aralash yem', 'Silos', 'Konsentrat', 'Pichan', 'Boshqa'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Narxi (so'm/kg)</label>
                <input type="number" className="form-input" value={form.cost_per_kg} onChange={e => set('cost_per_kg', e.target.value)} step="50" min="0" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Sana</label>
              <input type="date" className="form-input" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Bekor</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '...' : 'Saqlash'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HealthModal({ animalId, onClose, onSaved }: any) {
  const [form, setForm] = useState({ record_type: 'tekshiruv', date: new Date().toISOString().slice(0, 10), diagnosis: '', treatment: '', cost: '0', severity: 'past', veterinarian: '', resolved: false });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await api.addHealth(animalId, { ...form, cost: parseFloat(form.cost) });
    onSaved();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Sog'liq yozuvi qo'shish</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Turi *</label>
                <select className="form-select" value={form.record_type} onChange={e => set('record_type', e.target.value)}>
                  <option value="tekshiruv">Tekshiruv</option>
                  <option value="emlash">Emlash</option>
                  <option value="davolash">Davolash</option>
                  <option value="boshqa">Boshqa</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Og'irlik</label>
                <select className="form-select" value={form.severity} onChange={e => set('severity', e.target.value)}>
                  <option value="past">Past</option>
                  <option value="orta">O'rta</option>
                  <option value="yuqori">Yuqori</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Tashxis *</label>
              <input className="form-input" value={form.diagnosis} onChange={e => set('diagnosis', e.target.value)} required placeholder="Masalan: Nafas yo'li infeksiyasi" />
            </div>
            <div className="form-group">
              <label className="form-label">Davolash</label>
              <textarea className="form-textarea" rows={2} value={form.treatment} onChange={e => set('treatment', e.target.value)} placeholder="Dori-darmon, muolajalar..." />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Xarajat (so'm)</label>
                <input type="number" className="form-input" value={form.cost} onChange={e => set('cost', e.target.value)} min="0" step="1000" />
              </div>
              <div className="form-group">
                <label className="form-label">Shifokor</label>
                <input className="form-input" value={form.veterinarian} onChange={e => set('veterinarian', e.target.value)} placeholder="Dr. ..." />
              </div>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="resolved" checked={form.resolved} onChange={e => set('resolved', e.target.checked)} />
              <label htmlFor="resolved" style={{ fontSize: 14, cursor: 'pointer' }}>Yaxshilandi / Tugallandi</label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Bekor</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '...' : 'Saqlash'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
